/**
 * KiaanWidget — bottom-right on every AURA web page (spec 2026-10-16, item 1).
 *
 * LIVE SHAPE (Sun Stellar .83, 2026-10-23, plan 2026-10-23-kiaan-on-live.md): the widget is mounted by
 * AppShell only when GET /api/v1/kiaan/status says { enabled: true } for this person (useKiaanStatus).
 * Tabs: ✦ Agents · Communications · 🐞 Report a bug. Not on live: Dev mode, System monitor,
 * Calendar & meetings, Code map, page notes (those stay on the dev site).
 *
 * - Open by default; collapse state in localStorage['kiaan-open'] (try/catch).
 * - ⌘J / Ctrl+J toggles it (moved here from AppShell). `aura-ai-open` events
 *   open it on the Agents tab and stash the preset for the embedded AIPanel.
 * - Access is decided by `useKiaanAccess` (server permissions, fails closed). The last tab is
 *   remembered per user in localStorage (try/catch).
 * - Everything inside tolerates Kiaan Work being down; the AURA page never breaks.
 */
import { useCallback, useEffect, useState } from 'react';
import { Minus } from 'lucide-react';
import type { DashboardRoleKey } from '../../pages/dashboard/roleLayouts';
import { useKiaanAccess } from './useKiaanAccess';
import { KiaanTabs, TAB_KEYS, type KiaanTabKey } from './KiaanTabs';
import { KiaanTab } from './KiaanTab';
import { tourAudience } from '../../pages/tour/tourCatalog';
import { CommunicationsTab } from './CommunicationsTab';
import { ReportBugTab } from './ReportBugTab';
import { useAuthStore } from '@/stores/auth';
import './kiaan.css';

const OPEN_KEY = 'kiaan-open';
const PRESET_KEY = 'aura-ai-preset';

/** SUN-91: a file dropped inside the widget but outside a drop area must not make the browser open it. */
function keepFileInAura(e: React.DragEvent) {
  if (e.dataTransfer?.types?.includes('Files')) e.preventDefault();
}

export type KiaanReach = 'unknown' | 'ok' | 'down';

function readOpen(): boolean {
  try {
    const v = window.localStorage.getItem(OPEN_KEY);
    return v === null ? true : v === '1';
  } catch {
    return true;
  }
}

/** The last tab, per user: `kiaan-tab:<userId>`. Anything unknown → Agents. */
export const tabKey = (userId: string | null | undefined) => `kiaan-tab:${userId ?? 'anon'}`;

export function readTab(userId: string | null | undefined): KiaanTabKey {
  try {
    const v = window.localStorage.getItem(tabKey(userId));
    return v && (TAB_KEYS as string[]).includes(v) ? (v as KiaanTabKey) : 'agents';
  } catch {
    return 'agents';
  }
}

function writeTab(userId: string | null | undefined, tab: KiaanTabKey) {
  try {
    window.localStorage.setItem(tabKey(userId), tab);
  } catch {
    /* storage blocked — the widget still works */
  }
}

function writeOpen(open: boolean) {
  try {
    window.localStorage.setItem(OPEN_KEY, open ? '1' : '0');
  } catch {
    /* storage blocked — the widget still works */
  }
}

export interface KiaanWidgetProps {
  role: DashboardRoleKey | null | undefined;
  pathname: string;
  /** The admin's 'View as' choice. Any view except 'owner' shows Kiaan the way that role sees it. */
  previewView?: string | null;
}

export function KiaanWidget({ role, pathname, previewView = null }: KiaanWidgetProps) {
  const realAccess = useKiaanAccess();
  // 'View as' previews a non-admin role: show what that role gets (the server still decides for real users).
  const previewing = !!previewView && previewView !== 'owner';
  const access = previewing
    ? { ...realAccess, devMode: false, manageAgents: false, manageKeys: false, manageKnowledge: false, reportBug: true }
    : realAccess;
  const [open, setOpen] = useState<boolean>(readOpen);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  // SUN-110c: admin/owner (profile P18) sees the waiting requests; same rule as the App Tour's admin audience.
  const isAdminUser = useAuthStore((s) => tourAudience(s.user) === 'admin');
  const [tab, setTabState] = useState<KiaanTabKey>(() => readTab(userId));
  // A user picking a tab: remember it. Fallbacks below (lost access) do not overwrite the memory.
  const setTab = useCallback((t: KiaanTabKey) => { setTabState(t); writeTab(userId, t); }, [userId]);
  // Signed-in user changed (or arrived after the first paint) → their own last tab.
  useEffect(() => { setTabState(readTab(userId)); }, [userId]);
  const [wide, setWide] = useState(false);
  const [reach, setReach] = useState<KiaanReach>('unknown');

  // Live work (SUN-55): "Open <screen>" goes to that AURA screen. pushState + popstate is
  // how the app's router hears about it, so the widget needs no router of its own.
  const goTo = useCallback((route: string) => {
    if (window.location.pathname !== route) {
      window.history.pushState({}, '', route);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  }, []);
  const openScreenFromLiveWork = useCallback((route: string) => goTo(route), [goTo]);

  const toggle = useCallback(() => {
    setOpen((o) => {
      writeOpen(!o);
      return !o;
    });
  }, []);
  const collapse = useCallback(() => {
    setOpen(false);
    writeOpen(false);
  }, []);

  // ⌘J / Ctrl+J — kept from AppShell.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key.toLowerCase() !== 'j') return;
      e.preventDefault();
      toggle();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [toggle]);

  // Pages dispatch CustomEvent('aura-ai-open', {detail: {preset}}) to open the chat.
  useEffect(() => {
    const onAiOpen = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && typeof detail === 'object') {
        try { window.sessionStorage.setItem(PRESET_KEY, JSON.stringify(detail)); } catch { /* ignore */ }
      }
      setTab('agents');
      setOpen(true);
      writeOpen(true);
    };
    window.addEventListener('aura-ai-open', onAiOpen as EventListener);
    return () => window.removeEventListener('aura-ai-open', onAiOpen as EventListener);
  }, [setTab]);

  // A user who loses Report a bug (or never had it) must not sit on that tab. Wait for the permissions:
  // while they load every flag is false, and a remembered tab must survive that moment.
  useEffect(() => {
    if (realAccess.loading) return;
    if (tab === 'bug' && !access.reportBug) setTabState('agents');
  }, [tab, access.reportBug, realAccess.loading]);

  if (!open) {
    return (
      <div className="kw-root kw-collapsed" data-aura-chrome="kiaan-widget" data-tour="kiaan-widget">
        <button type="button" className="kw-fab" onClick={toggle} title="Open Kiaan (⌘J)" aria-label="Open Kiaan">
          K
        </button>
      </div>
    );
  }

  // SUN-70: the tab row needs the wide panel on every tab (the narrow one cut the tab row off).
  const panelClass = `kw-panel kw-wide${wide ? ' kw-xwide' : ''}`;

  return (
    <div
      className="kw-root"
      data-aura-chrome="kiaan-widget"
      data-tour="kiaan-widget"
      onDragOver={keepFileInAura}
      onDrop={keepFileInAura}
    >
      <div className={panelClass} role="dialog" aria-label="Kiaan">
        <div className="kw-head">
          <div className="kw-logo" aria-hidden="true">K</div>
          <span className="kw-title">Kiaan</span>
          <span
            className={`kw-dot${reach === 'down' ? ' kw-dot-off' : reach === 'unknown' ? ' kw-dot-busy' : ''}`}
            title={reach === 'ok' ? 'Kiaan Work is running' : reach === 'down' ? 'Kiaan Work is not reachable' : 'Checking Kiaan Work'}
            data-testid="kiaan-reach"
            data-reach={reach}
          />
          <span className="kw-sp" />
          <span className="kw-kbd">⌘J</span>
          <button type="button" className="kw-icon-btn" onClick={collapse} title="Collapse" aria-label="Collapse Kiaan">
            <Minus size={14} />
          </button>
        </div>

        <KiaanTabs active={tab} reportBug={access.reportBug} onChange={(t) => { setTab(t); setWide(false); }} />

        <div className="kw-body">
          {tab === 'agents' && (
            <KiaanTab
              role={role}
              pathname={pathname}
              onCollapse={collapse}
              onReach={setReach}
              canManageAgents={access.manageAgents}
              isAdmin={isAdminUser && !previewing}
              onWide={setWide}
              canAnswer={access.devMode}
              onMessageAgent={() => { setTab('comms'); setWide(false); }}
              onOpenScreen={openScreenFromLiveWork}
            />
          )}
          {tab === 'comms' && <CommunicationsTab isAdmin={access.devMode} canCallAgents={access.manageAgents} />}
          {tab === 'bug' && access.reportBug && <ReportBugTab pathname={pathname} />}
        </div>
      </div>
    </div>
  );
}
